import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  MenuItem, 
  Grid, 
  CircularProgress, 
  Alert, 
  InputLabel, 
  Select, 
  FormControl, 
  Checkbox, 
  FormControlLabel, 
  Switch, 
  OutlinedInput, 
  ListItemText, 
  IconButton, 
  Box, 
  Autocomplete, 
  Chip, 
  Divider, 
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  InputAdornment,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Transgender as GenderIcon,
  Class as ClassIcon,
  MenuBook as MenuBookIcon,
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { PrimaryButton } from '../ui/StyledComponents';
import supabase from '../../supabaseClient';

const emptyStudent = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  gender: '',
  class_ids: [],
  is_quran_student: true,
  is_greenbook_student: false,
  quran_completed: false,
  is_self_guardian: false,
};

function CreateStudentForm({ onSuccess }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [students, setStudents] = useState([{ ...emptyStudent }]);
  const [guardians, setGuardians] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedGuardians, setSelectedGuardians] = useState([]);
  const [guardianRelationships, setGuardianRelationships] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showGuardianSelect, setShowGuardianSelect] = useState(true);
  const today = new Date();

  useEffect(() => {
    const fetchGuardians = async () => {
      const { data, error } = await supabase.from('guardians').select('id, first_name, last_name');
      if (!error) setGuardians(data || []);
    };
    const fetchClasses = async () => {
      const { data, error } = await supabase.from('classes').select('id, name');
      if (!error) setClasses(data || []);
    };
    fetchGuardians();
    fetchClasses();
  }, []);


  const handleStudentChange = (idx, e) => {
    const { name, value, type, checked } = e.target;
    setStudents((prev) =>
      prev.map((student, i) =>
        i === idx
          ? {
              ...student,
              [name]: type === 'checkbox' ? checked : value,
            }
          : student
      )
    );
  };

  const handleClassChange = (idx, e) => {
    const { value } = e.target;
    setStudents((prev) =>
      prev.map((student, i) =>
        i === idx
          ? {
              ...student,
              class_ids: typeof value === 'string' ? value.split(',') : value,
            }
          : student
      )
    );
  };

  const addStudent = () => {
    setStudents((prev) => [...prev, { ...emptyStudent }]);
  };

  const removeStudent = (idx) => {
    setStudents((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    // Validation
    for (const student of students) {
      if (!student.is_self_guardian && selectedGuardians.length === 0) {
        setError('Please either select guardians or mark as self-guardian (if 18+).');
        setLoading(false);
        return;
      }

      if (student.is_self_guardian) {
        if (!student.date_of_birth) {
          setError('Date of birth is required for self-guardian students.');
          setLoading(false);
          return;
        }
        const birthDate = new Date(student.date_of_birth);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 18) {
          setError('Student must be 18 or older to be self-guardian.');
          setLoading(false);
          return;
        }
      }
    }
    for (const s of students) {
      if (!s.first_name || !s.last_name) {
        setError('First name and last name are required for all students.');
        setLoading(false);
        return;
      }
    }
    try {
      // Insert students with self-guardian status
      const inserts = students.map((s) => {
        const birthDate = s.date_of_birth ? new Date(s.date_of_birth) : null;
        const age = birthDate ? today.getFullYear() - birthDate.getFullYear() : 0;
        return {
          ...s,
          is_self_guardian: age >= 18 && s.is_self_guardian
        };
      });
      const { data: insertedStudents, error: studentError } = await supabase
        .from('students')
        .insert(inserts)
        .select();

      if (studentError) throw studentError;

      // Create guardian relationships in junction table using the relationship settings
      const relationshipsToInsert = [];
      insertedStudents.forEach((student) => {
        selectedGuardians.forEach((guardian) => {
          const relationship = guardianRelationships.get(guardian.id);
          relationshipsToInsert.push({
            student_id: student.id,
            guardian_id: guardian.id,
            is_primary: relationship?.is_primary || false,
            relationship_type: relationship?.relationship_type === 'Other' ? relationship?.otherType || 'Other' : relationship?.relationship_type || 'Mother'
          });
        });
      });

      const { error: relationshipError } = await supabase
        .from('student_guardians')
        .insert(relationshipsToInsert);

      if (relationshipError) throw relationshipError;

      setSuccess(true);
      const createdStudentIds = insertedStudents.map(s => s.id);
      setStudents([{ ...emptyStudent }]);
      setSelectedGuardians([]);
      if (onSuccess) onSuccess(createdStudentIds);
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  return (
    <Card
      sx={{
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
        borderRadius: 3,
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 30px rgba(0, 0, 0, 0.4)' 
            : '0 4px 16px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            pb: 2,
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(52, 152, 219, 0.2)' 
                : 'rgba(52, 152, 219, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonAddIcon 
              sx={{ 
                fontSize: { xs: 20, sm: 24 }, 
                color: theme.palette.primary.main 
              }} 
            />
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                color: theme.palette.text.primary,
              }}
            >
              Create Student
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mt: 0.5,
              }}
            >
              Add one or more students to the system
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
          {students.map((student, idx) => (
            <Grid item xs={12} key={idx} sx={{ 
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`, 
              borderRadius: 2, 
              mb: 2, 
              p: { xs: 2, sm: 3 },
            }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={student.first_name}
                    onChange={(e) => handleStudentChange(idx, e)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: '1.25rem',
                            }} 
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: 2,
                      },
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={student.last_name}
                    onChange={(e) => handleStudentChange(idx, e)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: '1.25rem',
                            }} 
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: 2,
                      },
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <TextField
                      fullWidth
                      label="Date of Birth"
                      name="date_of_birth"
                      type="date"
                      value={student.date_of_birth}
                      onChange={(e) => handleStudentChange(idx, e)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiInputBase-root': {
                          borderRadius: 2,
                        },
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        },
                      }}
                    />
                    {student.date_of_birth && new Date(student.date_of_birth) && 
                     (today.getFullYear() - new Date(student.date_of_birth).getFullYear()) >= 18 && 
                     selectedGuardians.length === 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={student.is_self_guardian}
                            onChange={(e) => handleStudentChange(idx, {
                              target: {
                                name: 'is_self_guardian',
                                type: 'checkbox',
                                checked: e.target.checked
                              }
                            })}
                          />
                        }
                        label="Self Guardian (18+ years old)"
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: 'relative' }}>
                    <GenderIcon 
                      sx={{ 
                        position: 'absolute',
                        left: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                        color: theme.palette.text.secondary,
                        fontSize: '1.25rem',
                        pointerEvents: 'none',
                      }} 
                    />
                    <FormControl 
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          pl: 5,
                        },
                      }}
                    >
                      <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Gender
                      </InputLabel>
                      <Select
                        name="gender"
                        value={student.gender}
                        onChange={(e) => handleStudentChange(idx, e)}
                        label="Gender"
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '& .MuiSelect-select': {
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          },
                        }}
                      >
                        <MenuItem value="Male" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Male
                        </MenuItem>
                        <MenuItem value="Female" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Female
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>

                {/* Guardian Section */}
                <Grid item xs={12}>
                  <Divider 
                    sx={{ 
                      my: 2,
                      borderColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.08)',
                    }} 
                  />
                  {/* <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>Guardian Information</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={student.is_self_guardian}
                          onChange={(e) => {
                            handleStudentChange(idx, {
                              target: {
                                name: 'is_self_guardian',
                                type: 'checkbox',
                                checked: e.target.checked
                              }
                            });
                            // Clear selected guardians if switching to self-guardian
                            if (e.target.checked) {
                              setSelectedGuardians([]);
                            }
                          }}
                          disabled={!student.date_of_birth || (new Date().getFullYear() - new Date(student.date_of_birth).getFullYear()) < 18}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">Self Guardian (18+ years old)</Typography>
                          {(!student.date_of_birth || (new Date().getFullYear() - new Date(student.date_of_birth).getFullYear()) < 18) && (
                            <Typography variant="caption" color="text.secondary">
                              Must be 18 or older to be self guardian
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box> */}
                  {!student.is_self_guardian && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* List of guardians with their relationship types */}
                        {selectedGuardians.map((guardian, index) => (
                          <Box key={guardian.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                            <Box sx={{ 
                              flex: 1, 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              p: 1.5, 
                              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`, 
                              borderRadius: 2,
                              backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.02)' 
                                : 'rgba(0, 0, 0, 0.02)',
                            }}>
                              <Typography sx={{ flex: 1 }}>
                                {guardian.first_name} {guardian.last_name}
                                {index === 0 && (
                                  <Chip
                                    label="Primary"
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Typography>
                            </Box>
                            
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>Relationship</InputLabel>
                              <Select
                                value={guardianRelationships.get(guardian.id)?.relationship_type || ''}
                                onChange={(e) => {
                                  const newRelationships = new Map(guardianRelationships);
                                  const currentRelationship = newRelationships.get(guardian.id) || {};
                                  newRelationships.set(guardian.id, {
                                    ...currentRelationship,
                                    relationship_type: e.target.value
                                  });
                                  setGuardianRelationships(newRelationships);
                                }}
                                label="Relationship"
                              >
                                <MenuItem value="Mother">Mother</MenuItem>
                                <MenuItem value="Father">Father</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                              </Select>
                            </FormControl>

                            {guardianRelationships.get(guardian.id)?.relationship_type === 'Other' && (
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Specify</InputLabel>
                                <Select
                                  value={guardianRelationships.get(guardian.id)?.otherType || ''}
                                  onChange={(e) => {
                                    const newRelationships = new Map(guardianRelationships);
                                    const currentRelationship = newRelationships.get(guardian.id) || {};
                                    newRelationships.set(guardian.id, {
                                      ...currentRelationship,
                                      relationship_type: 'Other',
                                      otherType: e.target.value
                                    });
                                    setGuardianRelationships(newRelationships);
                                  }}
                                  label="Specify"
                                >
                                  <MenuItem value="Aunt">Aunt</MenuItem>
                                  <MenuItem value="Uncle">Uncle</MenuItem>
                                  <MenuItem value="Grandfather">Grandfather</MenuItem>
                                  <MenuItem value="Grandmother">Grandmother</MenuItem>
                                  <MenuItem value="Brother">Brother</MenuItem>
                                  <MenuItem value="Sister">Sister</MenuItem>
                                </Select>
                              </FormControl>
                            )}

                            <IconButton
                              size="small"
                              onClick={() => {
                                const newGuardians = selectedGuardians.filter((_, i) => i !== index);
                                setSelectedGuardians(newGuardians);
                                const newRelationships = new Map(guardianRelationships);
                                newRelationships.delete(guardian.id);
                                setGuardianRelationships(newRelationships);
                              }}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Box>
                        ))}

                        {/* Add guardian button and selector */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
                            {(selectedGuardians.length === 0 || showGuardianSelect) && selectedGuardians.length < 2 ? (
                              <Autocomplete
                                fullWidth
                                options={guardians.filter(g => !selectedGuardians.find(sg => sg.id === g.id))}
                                getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                                value={null}
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    const defaultRelationType = newValue.gender === 'Male' ? 'Father' : 'Mother';
                                    const newRelationships = new Map(guardianRelationships);
                                    newRelationships.set(newValue.id, {
                                      relationship_type: defaultRelationType,
                                      is_primary: selectedGuardians.length === 0
                                    });
                                    setGuardianRelationships(newRelationships);
                                    setSelectedGuardians([...selectedGuardians, newValue]);
                                    setShowGuardianSelect(false);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField 
                                    {...params} 
                                    label="Select Guardian" 
                                    required={!student.is_self_guardian && selectedGuardians.length === 0}
                                    error={!student.is_self_guardian && selectedGuardians.length === 0}
                                    helperText={!student.is_self_guardian && selectedGuardians.length === 0 ? "Please select at least one guardian" : ""}
                                    size="small"
                                    sx={{
                                      '& .MuiInputBase-root': {
                                        borderRadius: 2,
                                      },
                                      '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                      },
                                      '& .MuiInputLabel-root': {
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                      },
                                    }}
                                  />
                                )}
                              />
                            ) : selectedGuardians.length === 1 ? (
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => setShowGuardianSelect(true)}
                              >
                                Add Another Guardian
                              </Button>
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <ClassIcon 
                      sx={{ 
                        position: 'absolute',
                        left: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                        color: theme.palette.text.secondary,
                        fontSize: '1.25rem',
                        pointerEvents: 'none',
                      }} 
                    />
                    <FormControl 
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          pl: 5,
                        },
                      }}
                    >
                      <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Class Assignments
                      </InputLabel>
                      <Select
                        multiple
                        name="class_ids"
                        value={student.class_ids}
                        onChange={(e) => handleClassChange(idx, e)}
                        input={<OutlinedInput label="Class Assignments" />}
                        renderValue={(selected) =>
                          classes
                            .filter((c) => selected.includes(c.id))
                            .map((c) => c.name)
                            .join(', ')
                        }
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '& .MuiSelect-select': {
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          },
                        }}
                      >
                        {classes.map((c) => (
                          <MenuItem key={c.id} value={c.id} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Checkbox checked={student.class_ids.indexOf(c.id) > -1} />
                            <ListItemText primary={c.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider 
                    sx={{ 
                      my: 2,
                      borderColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.08)',
                    }} 
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      height: '100%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.03)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                    }}
                  >
                    <MenuBookIcon 
                      sx={{ 
                        fontSize: 20, 
                        color: theme.palette.primary.main,
                        mr: 1,
                      }} 
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={student.is_quran_student}
                          onChange={(e) => handleStudentChange(idx, e)}
                          name="is_quran_student"
                          color="primary"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Quran Student
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      height: '100%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.03)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                    }}
                  >
                    <BookIcon 
                      sx={{ 
                        fontSize: 20, 
                        color: theme.palette.primary.main,
                        mr: 1,
                      }} 
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={student.is_greenbook_student}
                          onChange={(e) => handleStudentChange(idx, e)}
                          name="is_greenbook_student"
                          color="primary"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Greenbook Student
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      height: '100%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.03)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                    }}
                  >
                    <CheckCircleIcon 
                      sx={{ 
                        fontSize: 20, 
                        color: theme.palette.primary.main,
                        mr: 1,
                      }} 
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={student.quran_completed}
                          onChange={(e) => handleStudentChange(idx, e)}
                          name="quran_completed"
                          color="primary"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Quran Completed
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {students.length > 1 && (
                      <IconButton onClick={() => removeStudent(idx)} color="error" size="small" aria-label="Remove Student">
                        <RemoveIcon />
                      </IconButton>
                    )}
                    <IconButton onClick={addStudent} color="primary" size="small" aria-label="Add Student">
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          ))}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">Students created successfully!</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider 
              sx={{ 
                my: 2,
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.08)',
              }} 
            />
          </Grid>
          <Grid item xs={12}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 2,
                pt: 1,
              }}
            >
              <PrimaryButton
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '10px 24px', sm: '12px 32px' },
                  borderRadius: 2,
                  fontWeight: 500,
                  textTransform: 'none',
                  boxShadow: !loading
                    ? theme.palette.mode === 'dark'
                      ? '0 4px 12px rgba(52, 152, 219, 0.3)'
                      : '0 4px 12px rgba(52, 152, 219, 0.2)'
                    : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: !loading ? 'translateY(-2px)' : 'none',
                    boxShadow: !loading
                      ? theme.palette.mode === 'dark'
                        ? '0 6px 16px rgba(52, 152, 219, 0.4)'
                        : '0 6px 16px rgba(52, 152, 219, 0.3)'
                      : 'none',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={isMobile ? 20 : 24} color="inherit" />
                ) : (
                  'Create Students'
                )}
              </PrimaryButton>
            </Box>
          </Grid>
        </Grid>
      </form>
      </CardContent>
    </Card>
  );
}

export default CreateStudentForm; 